/*

Feature Notes:
- Get Params & State
 - Currently some state is being "stored" in get params. Eg:
  - Event type
  - Which client to select
  - Whether to view a specific event 
  - Until all non-react pages are ported to react/redux the "url state"
  will need to be passed into the filtered event select

*/

import { useSelector, useDispatch } from 'react-redux'
import React, { useEffect, useState, useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import {
	getEvents,
	updateFilters,
	selectFilteredEvents,
	setDeleteEvents,
	deleteEvents,
	clearFilters,
	updateSort,
	setShowDetails,
	getEventDetails,
	updateSelectedEventInfo,
	updatePrograms,
	updateNotesEditor,
	selectAllNotes
} from '../../state/events'
import { 
	getAllNotes,
} from '../../state/clientNotesSlice'
import { useGymTrainers } from '../users/hooks'
import useCurrentUser from '../../hooks/useCurrentUser'
import {
	getAllClients,
	selectUserById,
	selectClientsById,
	selectTrainerParam,
} from "../users/usersSlice"
import usePermissions from '../../hooks/usePermissions'
import { capitalize, extractEventParams, dateToYMD,envAwarePost } from '../../util/util'
import CenteredLoader from '../CenteredLoader/CenteredLoader'
import SectionHeading from '../sectionHeading/SectionHeading'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from "react-bootstrap/Tooltip"
import Popover from 'react-bootstrap/Popover'
import Dropdown from 'react-bootstrap/Dropdown'
import Table from 'react-bootstrap/Table'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import EventRow from './EventRow/EventRow'
import Templates from './Templates/Templates'
import PreviewModal from './PreviewModal/PreviewModal'
import EventComments from './EventComments/EventComments'
import { FullScreen as FullScreenRoutine } from './Routine/Routine'
import CopyModal from './CopyModal/CopyModal'
import { useDebounceCallback } from '@react-hook/debounce'
import Pagination from 'react-bootstrap/Pagination'
import useApiStatusDispatch from '../../hooks/useApiStatusDispatch'
import classes from './EventList.module.css'
import Spinner from 'react-bootstrap/Spinner'
import Row from 'react-bootstrap/Row'
import Select from 'react-select'
import FavoritesModal from '../FavoritesModal/FavoritesModal'
import Col from "react-bootstrap/Col"
import { useMapClientsToOptions } from '../../hooks/useMapClientsToOptions'
import ProgramsModal from './ProgramsModal/ProgramsModal'
import SelectEventType from './selectEventType/SelectEventType'
import EventCalendar from './EventCalendar/EventCalendar'
import ToggleButton from 'react-bootstrap/ToggleButton'
import { update } from 'lodash'
import NotesModal from './NotesModal/NotesModal'
import ResourceLibrary from '../ResourceLibrary/Library'

//maps: workout->workouts, task->tasks, nutrition->nutrtion
const eventTypeDisplayText = eventType => {
	switch(eventType){
		case 'nutrition': return 'nutrition'
		case 'all': return 'Events & Notes'
		default: return `${eventType}s`

	}
}

const statusDisplayText = value => ['open', 'complete', 'not met'][value]

const useEventHeadingText = (filter, client_id, eventType, isClient, trainer_id) => {
	const { status, trainers } = useGymTrainers()
	const client = useSelector(state => selectClientsById(state, client_id))
	if (isClient) {
		return `Your ${capitalize(eventTypeDisplayText(eventType))}`
	}
	if (filter === 'client') {
		if (client?.user_name) return `${client?.user_name}'s ${capitalize(eventTypeDisplayText(eventType))}`
	} else if (filter === 'gym_trainer') {
		const trainer = trainers.filter(t => parseInt(t.user_id_number) === parseInt(trainer_id))[0]
		return `${capitalize(trainer.user_name)}'s  ${capitalize(eventTypeDisplayText(eventType))}`
	} else {
		return `${capitalize(filter)}  ${capitalize(eventTypeDisplayText(eventType))}`
	}
}

//Complexity is O(M+N) which is still linear
const deletingAllEventsInPage = (toDelete, pageEvents) => {
	if(!pageEvents.length) return false
	//to prevent quadratic time: create a set for O(1) look upus
	//O(M) to create set
	//map to set of typed ides
	toDelete = new Set(
		//cancat into a single array
		[].concat(
			//map the entries into arrays of typed ids
			...Object.entries(toDelete).map(
				([eventType,ids])=>(
					ids.map(id=>(`${eventType}${id}`))
				)
			)
		)
	)
	//O(N) reduce
	return pageEvents.reduce((carry, {eventType,id}) => carry && toDelete.has(`${eventType}${id}`), true)
}

//select drop down for gyms & gym trainers
//to view each other's favorite events
const GymListOptions = ({ filter, trainer_id, eventType }) => {
	const history = useHistory()
	//get list of trainers
	const user = useCurrentUser()
	const { status, trainers } = useGymTrainers()
	//filter out current user form the list
	const filteredTrainers = useMemo(() => (
		trainers.filter(t => parseInt(t.user_id_number) !== parseInt(user.user_id_number))
	), [trainers])
	const userOptions = [
		{ value: user.user_id_number, label: 'My Favorites', filter: 'favorite' },
		{ value: user.gym_id, label: 'Gym Favorites', filter: 'gym' },
		...filteredTrainers.map(ft => (
			{
				value: ft.user_id_number,
				label: `${ft.user_name}'s Favorites`,
				filter: 'gym_trainer'
			}
		))
	]
	const handleChange = selectedOption => {
		let params = `?display=all&selector=${capitalize(eventTypeDisplayText(eventType))}&filter=${selectedOption.filter}`
		if (selectedOption.filter === 'gym_trainer') {
			params = `${params}&trainer_id=${selectedOption.value}`
		}
		history.push({ search: params })
	}

	return (
		//z-index = 4 so that drop down appears over pagination buttons
		//on small screens
		<div style={{ width: '200px', zIndex: 1000,position:'relative' }}>
			<Select
				defaultValue={{ value: user.user_id_number, label: 'My Favorites', filter: 'favorite' }}
				onChange={handleChange}
				options={userOptions}
			/>
		</div>
	)
}

const EventList = ({ location }) => {

	//misc hooks
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch();

	const history = useHistory()

	const [addingEvent,setAddingEvent] = useState(false)

	/* --- selectors --- */
	const user = useCurrentUser()
	const isClient = useSelector(state => state.users.current_user.account_type) === 'client'
	const isProspect = useSelector(state => state.users.current_user.account_type) === 'prospect'
	const isTrainer = useSelector(state => state.users.current_user.account_type) === "trainer"

	const permissions = useSelector(state => state.users.current_user.edit_permissions)
	const trainerParam = useSelector(selectTrainerParam)
	const isPartOfGym = useSelector(state => state.users.current_user.gym_id != 0)

	const clientIdUser = useSelector(state => state.users.current_user.user_id_number)

	const [queryParamSingular, setQueryParamSingular] = useState("");

	const [selectedClient, setSelectedClient] = useState({ empty: true })
	const [selectEventShow, setSelectEventShow] = useState(false);

	const [noteModalShow, setNoteModalShow] = useState(false);


	const [searchQuery, setSearchQuery] = useState("");
	//eventType = workout | task | nutrition
	//display = all | <event_id>
	//filter = client | favorite | all
	const {
		eventType: paramEventType,
		display: paramDisplay,
		filter: paramFilter,
		client_id: paramClient_id,
		trainer_id: paramTrainer_id,
		origin: paramOrigin,
		calendarEventType: paramCalendarEventType,
		view: paramView
	} = extractEventParams(location.search)


	// determine if should provide return link client or my calendar
	let calendarOrigin = false;
	let calendarLink;
	if (paramOrigin === "EventCalendar" || paramOrigin == "myCalendar") {
		calendarOrigin = true
		calendarLink =
			// if Event Calendar then go to /calendar
			paramOrigin === "EventCalendar" ? "/calendar" :
				// if My Calendar then go to /scheduler
				"/scheduler"
	}

	const {
		selectedEventTypeList: eventType,
		selectedDisplay: display,
		selectedFilter: filter,
		selectedClient: client_id,
		selectedTrainer_id: trainer_id,
		selectedEventTypeCalendar: eventTypeCalendar,
		isCalendarView,
	} = useSelector(state => (state.allEvents.selectedEventInfo))

	const showAllEventsAllClients = filter === 'all'
	const isFavorites = client_id === "favorite";


	//takes clientlist and returns an array of select objects

	const clientListEntities = useSelector(state => state.users.clients.entities);
	const [clientList, setClientList] = useState([]);

	//gets clientlist from store then converts to array and sorts by name
	useMemo(() => {
		let sortedList = Object.values(clientListEntities).sort(function (a, b) {
			let textA = a.user_name.toUpperCase();
			let textB = b.user_name.toUpperCase();
			return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
		})
		setClientList(sortedList);
	}, [clientListEntities])

	let mapClientsToSelectObjects = useMapClientsToOptions(clientList);

	//select correct client
	useEffect(() => {
		if (isClient || isProspect) {
			dispatch(updateSelectedEventInfo({ selectedClient: clientIdUser }))
			// if client_id set in params use it
		} else if (paramClient_id) {
			handleSelectClient(Number(paramClient_id))
		} else if (!(isClient||isProspect) && (isFavorites || showAllEventsAllClients)) {
			// if not client and is favorite or 'all events all users', remove client_id 
			setSelectedClient({ empty: true })
			dispatch(updateSelectedEventInfo({ selectedClient: 'favorite' }))
		} else {
			//  default to redux client_id
			handleSelectClient(client_id)
		}
	}, [clientList, paramClient_id, isFavorites, showAllEventsAllClients, isClient,isProspect])


	function determineSelectedEventInfo() {

		let selectedEventTypeList =
			// if param set then use it
			paramEventType ? paramEventType :
				// if event set then use it
				eventType ? eventType :
					// default to workout 
					"workout";

		// if calendar param set then use it, 
		let selectedEventTypeCalendar = paramCalendarEventType ? paramCalendarEventType :
			// if list param set then use it, 
			paramEventType ? paramEventType :
				//default to redux state
				eventTypeCalendar


		// set view using params if exist, otherwise default to redux state
		let selectedIsCalendarView = paramView ? paramView !== "list" : isCalendarView
		let selectedTrainer_id = paramTrainer_id
		let selectedDisplay = paramDisplay
		let selectedFilter = paramFilter

		return {
			selectedEventTypeList,
			selectedEventTypeCalendar,
			selectedDisplay,
			selectedFilter,
			selectedTrainer_id,
			selectedIsCalendarView,
			selectedClient:selectedFilter==='favorite'?'favorite':client_id
		}
	}

	// update selected info with params, redux state, or default
	useEffect(() => {
		const update = determineSelectedEventInfo()

		dispatch(updateSelectedEventInfo(
			{
				selectedEventTypeList: update.selectedEventTypeList,
				selectedEventTypeCalendar: update.selectedEventTypeCalendar,
				selectedDisplay: update.selectedDisplay,
				selectedFilter: update.selectedFilter,
				selectedTrainer_id: update.selectedTrainer_id,
				isCalendarView: update.selectedIsCalendarView,
				selectedClient:update.selectedClient
			}
		))
	}, [paramEventType, paramDisplay, paramFilter, paramTrainer_id, paramCalendarEventType, paramView])

	useEffect(() => {
		if (eventType == "Workouts") {
			setQueryParamSingular("Workout");
		} else if (eventType == "Tasks") {
			setQueryParamSingular("Task");
		} else if (eventType == "Nutrition" || eventType == "Nutritions" || eventType == 'nutritions') {
			setQueryParamSingular("Nutrition");
		} else {
			setQueryParamSingular(capitalize(eventType))
		}
	}, [eventType])

	const favoriteOrGymFilter = ['favorite', 'gym', 'gym_trainer'].includes(filter)


	//get status of events loading
	const status = useSelector(state => state.allEvents.status)

	//custom selector which returns all filtered events
	//or if display param is set will only display the event
	//with the specified id
	const eventIds = useSelector( state => (
		selectFilteredEvents(state, eventType, display, filter, client_id, trainer_id)
	))

	//get client 
	let headingEventType = eventType

	const headingText = useEventHeadingText(filter, client_id, headingEventType, isClient||isProspect, trainer_id)

	const { by: sortBy, desc } = useSelector(state => state.allEvents.sort)
	const { toDelete } = useSelector(state => state.allEvents)
	const { upcomingOnly, status: statusFilter } = useSelector(state => state.allEvents.filters)

	const [isClientListLoading, setIsClientListLoading] = useState(false)

	//firstGif 
	useEffect(() => {
		setFirstGif(true)
	}, [eventType])

	// Event success modal state 
	const [firstGif, setFirstGif] = useState(false);

	/* --- state --- */
	//templates state
	const [showTemplates, setShowTemplates] = useState(false)

	//filter popover state
	const [showFilters, setShowFilters] = useState(false)

	//local pagination state (0 indexed for functionality, 1 indexed for UI)
	const [page, setPage] = useState(0)
	const [eventsPerPage, setEventsPerPage] = useState(100)
	const pageCount = Math.ceil(eventIds.length / eventsPerPage)
	const startEventIdx = page * eventsPerPage
	const endEventIdx = startEventIdx + eventsPerPage

	//slice filtered events for pagination
	const pageEvents = eventIds
		//get those in range
		.slice( startEventIdx, endEventIdx )
		//prevent nulls
		.filter( i => Boolean(i) )

	const deleteAll = deletingAllEventsInPage(toDelete, pageEvents)


	// for fav event modal
	const [addFavoriteEvent, setAddFavoriteEvent] = useState(false)

	useEffect(() => {
		setFavEventTypeChosen(
			//default to workout if all is selected
			eventType === 'all'
			? 'workout'
			: eventType
		)
	}, [eventType])

	const [favEventTypeChosen, setFavEventTypeChosen] = useState("")

	const [loadingStatus,setLoadingStatus] = useState('idle')

	function dispatchGetEvents() {
		setLoadingStatus('pending')
		const eventTypes = (
			eventType === 'all'
			? ['workout','task','nutrition']
			: [eventType] 
		).filter(t=>t!=='note')

		return Promise.all([
			dispatch(getAllNotes({client_id})),
			...eventTypes.map(
				event_type => {
					const payload = {
						event_type,
						//sometimes client_id will be null (eg opening a link or reloading)
						//use the client_id specified in the url in this case
						client_id:isClient||isProspect?user.user_id_number:(client_id||paramClient_id),
						filter
					}
					if(Boolean(trainer_id)){
						payload.trainer_id = trainer_id
					}
					return dispatch(getEvents(payload))
				}
			)
		]).then(()=>{
			setLoadingStatus('idle')
		})
	}

	/* --- effects --- */

	//get all events
	useEffect(() => {
		if (eventType && !isCalendarView) {
			dispatchGetEvents()
		}
	}, [eventType, display, filter, client_id, trainer_id, isCalendarView])


	//ensure that clients are loaded for this feature
	useEffect(() => {
		dispatch(getAllClients({include_compliance:0}))
	}, [])

	//if display value is not 'all' then open the single event
	//that is being displayed (usefull when clicking on "view" button
	//on todays events simplified nutrition). Additionally, make
	//sure all events are loaded before attempting to open details
	useEffect(() => {
		if(!Boolean(display)||!Boolean(eventType)) return
		if (display !== 'all' && status === 'loaded') {
			dispatch(setShowDetails({
				type: eventType,
				showDetails: true,
				id: display
			}))
			// get the routine details of single display event
			dispatch(
				getEventDetails({
					event_type: eventType,
					event_id: display,
				})
			);
		}
	}, [display, status])

	/* --- Handlers --- */

	const setDisplayToAll = () => {
		const params = new URLSearchParams(location.search)
		params.set('display', 'all')
		history.push({
			pathname: '/eventsView',
			search: `?${params.toString()}`
		})
	}
	//comment
	const handleSortBy = e => {
		setPage(0)
		const by = e.target.value
		dispatch(updateSort({ by }))
	}

	const handleDeleteEvents = () => {
		if (!window.confirm(`Are you sure you want to delete ALL ${toDelete.length} selected events?`)) return
		setPage(0)
		statusDispatch(deleteEvents(eventType), eventType, "delete")
	}

	const debouncedSearchHandler = useDebounceCallback(query => {
		setPage(0)
		dispatch(updateFilters({ query }))
	}, 200)

	//upon selecting a client, finds the client object
	const handleSelectClient = (selectedUserid) => {
		let selected = clientList.find((client) => client.user_id_number === selectedUserid);
		if (selected) {
			setSelectedClient(selected)
			const payload = { selectedClient: selected.user_id_number}
			if(!selectedClient.empty&&selectedClient.user_id_number!==selected.user_id_number){
				payload.selectedDisplay = 'all'
			}
			dispatch(updateSelectedEventInfo(payload))
		}
	}

	const addInactiveLabel = (client) => {
		return client.inactivate_account ? " (inactive)" : ""
	}

	const renderMobileDropdown = () => {

		return <div className={classes.mobileAddButtonDropdown}>
			<Dropdown variant="outline-primary" className="d-inline mx-2">
				<Dropdown.Toggle disabled={addingEvent} id="dropdown-autoclose-true">
					Add Options {addingEvent&&<Spinner animation="border" size="sm"/>}
				</Dropdown.Toggle>

				<Dropdown.Menu>
					{isTrainer && isCalendarView &&
						<Dropdown.Item
							onClick={() => { 
								dispatch(updateNotesEditor({
									show:true,
									clientId:client_id
								}))
							}}>
							Add Note
						</Dropdown.Item>}


					{(!(isClient||isProspect)) &&
						<Dropdown.Item
							onClick={() => { setShowTemplates(true) }}>
							Add Template
						</Dropdown.Item>
					}
					{(!(isClient) || permissions[eventType] == "1") && !isProspect && !isFavorites &&
						<Dropdown.Item
							onClick={() => { setAddFavoriteEvent(true) }}>
							Add Favorite
						</Dropdown.Item>}
					{!(isClient||isProspect) && isFavorites &&
						<Dropdown.Item
							as={Link}
							to={`/programs?${trainerParam}`
						}>
							View Programs
						</Dropdown.Item>}
					{!(isClient||isProspect) && !isFavorites &&
						<Dropdown.Item
							onClick={() => {
								dispatch(updatePrograms({ show: true }))
							}}>
							Add Program
						</Dropdown.Item>
					}
					{((!(isClient) || permissions[eventType] == "1") && !isProspect && !showAllEventsAllClients) &&
						<div>
							{eventTypeCalendar === "all" ?
								<Dropdown.Item
									onClick={() => setSelectEventShow(true)}>
									Add Event
								</Dropdown.Item> :
								<Dropdown.Item
									onClick={() => {
										setAddingEvent(true)
										envAwarePost('api/events/',{
											action: 'createEvent',
											is_favorite: client_id === 'favorite' ? 1 : 0,
											event_type: eventType,
											date: client_id === 'favorite' ? "1969-12-31" : dateToYMD(new Date()),
											title: '',
											client_id
										}).then(res=>{
											setAddingEvent(false)
											if(res.success==='0'){
												window.alert(res.message)
												return
											}
											console.log(res)
											const event_id = res.data[`${eventType}_id`]
											const editorRoute = `/eventEditor/?event_id=${event_id}&event_type=AddOrEdit&eventType=${capitalize(eventType)}&client=${client_id ? client_id : isClient ? clientIdUser : 'favorite'}&${trainerParam}`
											
											history.push(editorRoute)
										})
										/*
										*/
									}}
								>
									Add {queryParamSingular}
								</Dropdown.Item>
							}
						</div>
					}
				</Dropdown.Menu>
			</Dropdown>
		</div >
	}

	const renderButtonBlock = () => (
		<div className={`${classes.AddButtonsBlock}`} >
			{isTrainer &&
				<Button
					size="sm"
					variant="outline-warning"
					style={{ color: "#d18803" }}
					className='ml-2 mt-2'
					onClick={() => { 
						dispatch(updateNotesEditor({
							show:true,
							clientId:client_id
						}))
					}}>
					Add Note
				</Button>
			}
			{(!(isClient||isProspect)&&eventType==='workout') &&
				<Button
					onClick={() => { setShowTemplates(true) }}
					className="ml-2 mt-2"
					variant="outline-primary"
					size="sm"
				>
					Add Template
				</Button>
			}

			{(!(isClient) || permissions[eventType] == "1") && !isProspect && !isFavorites &&
				<Button
					variant="outline-primary"
					size="sm"
					className="ml-2  mt-2"
					onClick={() => { setAddFavoriteEvent(true) }}>
					Add Favorite
				</Button>
			}

			{!(isClient||isProspect) && isFavorites &&
				<Button
					as={Link}
					className="ml-2 mt-2"
					variant="outline-primary"
					to={`/programs?${trainerParam}`}
					size="sm"
				>
					View Programs
				</Button>}

			{!(isClient||isProspect) && !isFavorites &&
				<Button
					size="sm"
					variant="outline-primary"
					className='ml-2  mt-2'
					onClick={() => {
						dispatch(updatePrograms({ show: true }))
					}}>
					Add Program
				</Button>}


			{((!(isClient) || permissions[eventType] == "1") && !isProspect && !showAllEventsAllClients) &&
				<>
					{eventTypeCalendar === "all" ?
						<Button
							className="ml-1  mt-2"
							variant="outline-success"
							onClick={() => setSelectEventShow(true)}
							size="sm"
						>
							Add Event
						</Button>
						:
						<Button
							disabled={addingEvent}
							className="ml-1  mt-2"
							variant="outline-success"
							onClick={() => {
								setAddingEvent(true)
								envAwarePost('api/events/',{
									action: 'createEvent',
									is_favorite: client_id === 'favorite' ? 1 : 0,
									event_type: eventType,
									date: client_id === 'favorite' && !(isClient||isProspect) ? "1969-12-31" : dateToYMD(new Date()),
									title: '',
									client_id: isClient ? user.user_id_number : client_id
								}).then(res=>{
									setAddingEvent(false)
									if(res.success==='0'){
										window.alert(res.message)
										return
									}
									console.log(res)
									const event_id = res.data[`${eventType}_id`]
									const editorRoute = `/eventEditor/?event_id=${event_id}&event_type=AddOrEdit&eventType=${capitalize(eventType)}&client=${isClient ? user.user_id_number :(client_id ? client_id : 'favorite')}&${trainerParam}`
									
									history.push(editorRoute)
								})
								/*
								*/
							}}
							size="sm"
						>
							Add {queryParamSingular} {addingEvent&&<Spinner animation="border" size="sm"/>}
						</Button>}
				</>}
		</div>
	)

	//array of available event types
	const eventOptions = [
		{ label: 'All', value: 1 },
		{ label: 'Workout', value: 2 },
		{ label: 'Nutrition', value: 3 },
		{ label: 'Task', value: 4 },
	]

	if (filter === 'client') eventOptions.push({ label: 'Note', value: 5 })


	function clearSearch() {
		setSearchQuery("");
		dispatch(clearFilters())
	}

	//upon type selection sets the selectedType state
	const handleTypeSelect = (type) => {

		clearSearch();

		// if calendar view and all events selected, only update
		// selected event for calendar NOT for list. 
		let payload = {
			selectedEventTypeList: type,
			selectedEventTypeCalendar: type,
			selectedDisplay: "all"
		}
		dispatch(updateSelectedEventInfo(payload))

	}

	/* --- render stuff --- */

	//define pagination stuff here since we'll use it twice
	//(above & below the table)
	const Pages = () => {
		if (pageCount <= 1) return null
		//calculate range to display
		//max number of pages to show in selection (must be odd number)
		const selectionCount = 5
		const selectionOffset = (selectionCount - 1) / 2
		const selectionStart = Math.min(Math.max(page - selectionOffset, 1), pageCount - selectionCount - 1)
		const selectionEnd = Math.min(Math.max(page + selectionOffset, selectionStart + selectionCount), pageCount - 1)
		const generatePageArray = () => {
			let loopStart, loopEnd
			if (pageCount <= selectionCount) {
				loopStart = 0
				loopEnd = pageCount
			} else {
				loopStart = selectionStart
				loopEnd = selectionEnd
			}
			const pages = []
			for (let i = loopStart; i < loopEnd; i++) {
				pages.push(i)
			}
			return pages
		}
		const pages = generatePageArray()

		return (
			<div>
				<div style={{ listStyleType: 'none' }} className="d-flex justify-content-center">
					<Pagination size="sm">
						<Pagination.Prev
							onClick={() => {
								setPage(page - 1)
							}}
							disabled={page === 0}
						/>
						{pages[0] !== 0 &&
							<Pagination.Item
								active={0 === page}
								onClick={() => {
									setPage(0)
								}}
							>
								1
							</Pagination.Item>
						}
						{(pages[0] > 1 && pageCount > selectionCount) &&
							<Pagination.Ellipsis
								onClick={() => {
									setPage(
										Math.max(pages[0] - 1, 1)
									)
								}}
							/>
						}
						{pages.map(i => (
							<Pagination.Item
								active={i === page}
								onClick={() => {
									setPage(i)
								}}
								key={i}
							>
								{i + 1}
							</Pagination.Item>
						))}
						{(pages[pages.length - 1] < (pageCount - 2) && pageCount > selectionCount) &&
							<Pagination.Ellipsis
								onClick={() => {
									setPage(
										Math.min(pages[pages.length - 1] + 1),
										pageCount - 1
									)
								}}
							/>
						}
						{pageCount > selectionCount &&
							<Pagination.Item
								active={(pageCount - 1) === page}
								onClick={() => {
									setPage(pageCount - 1)
								}}
							>
								{pageCount}
							</Pagination.Item>
						}
						<Pagination.Next
							onClick={() => {
								setPage(page + 1)
							}}
							disabled={page === (pageCount - 1)}
						/>
					</Pagination>
				</div>
				<div style={{ fontSize: "12.8px" }} className="mt-2 text-center">
					<i className="fas fa-circle mr-1" style={{ color: 'rgb(103 209 206)' }}>
					</i>Workouts | <i className="fas fa-circle mr-1" style={{ color: 'rgb(0 223 26)' }}>
					</i>Tasks | <i className="fas fa-circle mr-1" style={{ color: 'rgb(255 156 33)' }}>
					</i>Nutrition | <i className="fas fa-circle mr-1" style={{ color: "#ffeb3b" }}>
					</i> Notes | <i className="far fa-circle mr-1" style={{ color: "rgba(255, 0, 0, 0.57)" }}></i> Draft
				</div>
			</div>
		)
	}

	console.log(client_id)

	return (
		<>
			<div>
				<div className="p-2 p-md-3">
					{(isTrainer && !isFavorites) && 
						<div className="d-flex">
							<div>
								<Link to={"/trainer"}>
									<small>Back to Client List</small>
								</Link>
							</div>
						</div>
					}
					{isFavorites &&
						<div className="d-flex">
							<strong>Favorites</strong>
						</div>
					}
					<div className="d-flex align-items-end flex-wrap">
						{!showAllEventsAllClients && <div>
							<div className="d-flex w-100 align-items-center">
								{!favoriteOrGymFilter &&
									<>
										{isClientListLoading && !isTrainer ?
											<div>
												<Spinner animation="border" variant="primary" />
											</div>
											:
											<>
												{isTrainer &&
													<div className="mr-2" style={{zIndex: 4,position:'relative', width:'200px'}}>
														<small className='p-form-label'>Select a client:</small>
														<Select
															size="sm"
															options={mapClientsToSelectObjects}
															placeholder='select a client'
															value={{
																label: selectedClient.user_name ? selectedClient.user_name : null,
																value: selectedClient.user_id_number,
																key: selectedClient.user_id_number,
															}}
															onChange={(e) => {
																const params = new URLSearchParams(window.location.search)
																params.set('client',e.value)
																history.push({
																	path:'/eventsView',
																	search: params.toString()
																})
																handleSelectClient(e.value)
															}}
														/>
													</div>
												}
											</>
										}
									</>}

								{(!selectedClient.empty || isClient || favoriteOrGymFilter) ?
									<div style={{position:'relative'}}>
										<small className='p-form-label'>Event Type: </small>
										<div  className="d-flex align-items-center">
											
											<div className="mr-2" style={{ width:'124px',position:'relative',zIndex:1001}}>
												<Select
													value={{
														label: eventType,
														value: 99
													}}
													options={eventOptions}
													onChange={(e) => handleTypeSelect(e.label.toLowerCase())}
												/>
											</div>
										</div>
									</div>
									: !isFavorites ?
										<Col>
											<p style={{ visibility: 'hidden', marginBottom: '0' }}>placeholder</p>
											<strong>Select a client to view their calendar</strong>
										</Col>
										: null
								}

							</div>

						</div>}
						{(!selectedClient.empty || isClient || favoriteOrGymFilter) && <div className="ml-auto mt-2">
						{(!favoriteOrGymFilter || isClient) && <ButtonGroup size="sm">

							<Button
								variant={!isCalendarView ? "outline-primary" : "primary"}
								onClick={() => {
									dispatch(updateSelectedEventInfo({ isCalendarView: true, selectedDisplay: "all" }))
									clearSearch();
								}}
							>
								<i className="fas fa-calendar"/> Calendar View
							</Button>

							<Button
								variant={isCalendarView ? "outline-primary" : "primary"}
								onClick={() => {
									dispatch(updateSelectedEventInfo({ isCalendarView: false }))

								}}
							>
								<i className="fas fa-list"/> List View
							</Button>

							</ButtonGroup>}
						</div>}
						<div className="ml-auto">
							{/* dropdown of links for mobile view, visibility controlled by @media query */}
							{renderMobileDropdown()}
							{/* button block of links for larger screens, visibility controlled by @media query */}
							{renderButtonBlock()}
						</div>
					</div>
				</div>




				{isCalendarView ? <EventCalendar setSelectEventShow={setSelectEventShow} setNoteModalShow={setNoteModalShow} noteModalShow={noteModalShow} /> :
					<>
						<div className="d-flex mb-1 align-items-center px-2 px-md-3">
							<Form.Control
								className="mr-2"
								placeholder="search"
								value={searchQuery}
								onChange={e => {
									setSearchQuery(e.target.value)
									debouncedSearchHandler(e.target.value)
								}}
							/>
							<OverlayTrigger
								show={showFilters}
								placement="left-start"
								trigger="click"
								overlay={
									<Popover>
										<Popover.Title as="h3">Filter {capitalize(eventTypeDisplayText(eventType))}</Popover.Title>
										<Popover.Content>
											<Form.Check
												onChange={e => {
													setPage(0)
													dispatch(updateFilters({
														upcomingOnly: e.target.checked
													}))
												}}
												checked={upcomingOnly}
												type="checkbox"
												id="toggle-upcoming"
												label="Only show upcoming"
											/>
											<small>status</small>
											<Form.Control
												size="sm"
												as="select"
												value={statusFilter}
												onChange={e => {
													setPage(0)
													dispatch(updateFilters({
														status: e.target.value
													}))
												}}
											>
												<option value={'any'}>Any</option>
												<option value={1}>Complete</option>
												<option value={2}>Not Met</option>
												<option value={0}>Open</option>
											</Form.Control>
										</Popover.Content>
									</Popover>
								}
							>
								<OverlayTrigger overlay={
									<Tooltip>
										Filter {capitalize(eventTypeDisplayText(eventType))}
									</Tooltip>
								}>
									<Button
										style={{ width: '44px' }}
										onClick={() => {
											setShowFilters(!showFilters)
										}}
										variant={showFilters ? "outline-danger" : "outline-primary"}
									>
										<i className={showFilters ? "fa fa-times" : "fas fa-filter"} />
									</Button>
								</OverlayTrigger>
							</OverlayTrigger>
						</div>
						<div className="d-flex mb-2 align-items-center flex-wrap justify-content-between px-2 px-md-3">
							{(favoriteOrGymFilter && isPartOfGym && !(isClient||isProspect)) &&
								<GymListOptions filter={filter} eventType={eventType} trainer_id={trainer_id} />
							}
							{(!favoriteOrGymFilter || isClient) &&
								<div className="d-flex mb-2 align-items-center">
									<div style={{ flex: '0 0 auto' }} className="mr-2">
										sort by:
									</div>
									<Form.Check
										checked={sortBy === 'date'}
										onChange={handleSortBy}
										value="date"
										inline
										label="Date"
										name="event-sort"
										type="radio"
										id="date-sort"
									/>
									<Form.Check
										value="name"
										inline={sortBy === 'name'}
										onChange={handleSortBy}
										label="Name"
										name="event-sort"
										type="radio"
										id="name-sort"
									/>

									<Button
										onClick={() => {
											//toggles descending or not
											dispatch(updateSort({ desc: !desc }))
										}}
										className="text-dark ml-2"
										variant="link"
										size="sm"
									>
										<i className="fas fa-sort" />
									</Button>
								</div>
							}

						</div>
						{(upcomingOnly || statusFilter !== 'any' || display !== 'all') &&
							<div className="mb-2 d-flex align-items-center">
								Currently only viewing filtered {(eventTypeDisplayText(eventType))}
								{display !== 'all' &&
									<Button
										//dispatch clear filters
										onClick={() => {
											setDisplayToAll()
											dispatch(clearFilters())
										}}
										variant="link"
										size="sm"
									>
										view all
									</Button>
								}
								{upcomingOnly &&
									<Button
										onClick={() => {
											setPage(0)
											dispatch(updateFilters({ upcomingOnly: false }))
										}}
										variant="link"
										size="sm"
									>
										clear upcoming filter
									</Button>
								}
								{statusFilter !== 'any' &&
									<Button
										onClick={() => {
											setPage(0)
											dispatch(updateFilters({ status: 'any' }))
										}}
										variant="link"
										size="sm"
									>
										clear {statusDisplayText(statusFilter)} status filter
									</Button>
								}

							</div>

						}
						<Pages />
						<Table bordered className={classes.EventListTable}>
							<thead>
								<tr className="text-light" style={{ background: '#00041c' }}>
									<td>
										{capitalize(eventTypeDisplayText(eventType))}
									</td>
									{showAllEventsAllClients &&
										<td>
											Client Name
										</td>
									}
									<td>
										Options
									</td>
									<td style={{ width: '70px' }}>
										{!['gym', 'gym_trainer'].includes(filter) &&
											<div className="d-flex align-items-center">
												<Form.Check
													className="mr-2"
													onChange={(e) => {
														//IMPORTANT: only selects pages IN VIEW
														dispatch(setDeleteEvents(deleteAll ? [] : pageEvents))
													}}
													checked={deleteAll}
													type="checkbox"
													aria-label="check all"
												/>
												<Button
													disabled={toDelete.length === 0}
													onClick={handleDeleteEvents}
													variant="danger"
													size="sm">
													<i className="fa fa-trash" />
												</Button>
											</div>
										}
									</td>
								</tr>
							</thead>
							<tbody>
								{
									loadingStatus === 'idle'
									? pageEvents.map(({eventType,id}) => {
										let rowFilter = isClient ? clientIdUser : filter;
										return (
											<EventRow
												key={id}
												id={id}
												eventType={eventType}
												filter={rowFilter}
												setFirstGif={setFirstGif}
												firstGif={firstGif}
											/>)
									})//end map
									: (
										<tr className="text-center p-5">
											<td colSpan="100%">
												<Spinner animation="border" />
											</td>
										</tr>
									)
								}
							</tbody>
						</Table>

						<Pages />
					</>}
				<PreviewModal />
				<EventComments />
				<FullScreenRoutine />
				<CopyModal />
				<NotesModal/>
				<SelectEventType
					selectEventShow={selectEventShow}
					setSelectEventShow={setSelectEventShow}
				/>
				<FavoritesModal
					// show or hide modal
					addFavoriteEvent={addFavoriteEvent}
					setAddFavoriteEvent={setAddFavoriteEvent}
					//what event is selected
					favEventTypeChosen={favEventTypeChosen}
					setFavEventTypeChosen={setFavEventTypeChosen}
					//what client to copy favorites to
					selectedClient={client_id}
					// show option to choose between nutrition, tasks, and workouts
					allEventTypeOptions={isCalendarView ? true : false}
				// this is where to redirect after form submit in favorites modal
				//origin="EventCalendar"
				/>
				<ProgramsModal dispatchGetEvents={dispatchGetEvents} />
				{!(isClient||isProspect) &&
					<Templates
						client_id={client_id}
						show={showTemplates}
						setShow={setShowTemplates}
						eventType={
							//default to workout if all is selected
							eventType==='all'
							?'workout'
							:eventType
						}

					/>
				}
			</div>
			<ResourceLibrary/>
		</>
	)
}

export default EventList